import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import { useCallback } from 'react'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { resolvePercentDecimals } from '@aytm/app/stats/page/components/lib/utils'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

import { getColorTheme } from '@aytm/app/stats/lib/stat-page'

import { calcControlsProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/controls'
import { calcDownloadParams } from '@aytm/app/stats/page/question-views/md/lib/export-params'
import { calcTableProps } from '@aytm/app/stats/page/question-views/md/lib/table-utils'
import useMaxDiffState from '@aytm/app/stats/page/question-views/md/states/useMaxDiffState'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import ItemsControls from '@aytm/app/stats/page/question-views/components/controls/Controls'
import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'

import Controls from '@aytm/app/stats/page/question-views/md/components/Controls'
import QuestionIcon from '@aytm/app/stats/page/question-views/md/components/QuestionIcon'
import Chart from '@aytm/app/stats/page/question-views/md/components/chart/Chart'

// How many items a respondent saw per screen, for a question that never declared a number.
const DEFAULT_ITEMS_PER_SET = 4

const RUN_NOTE = 'We need at least 150 survey completes to calculate Advanced MaxDiff HB'

// the badge the run band carries while the question has nothing to draw
const RUN_ICON = (
  <>
    <QuestionIcon />
    <span>
      Advanced
      <br />
      MaxDiff&nbsp;HB<sup>SM</sup>
    </span>
  </>
)

// The items list carries two sortable columns; the token the header pick produces is the
// same one the persistence layer round-trips (item-sorted `i*`, value-sorted `v*`).
const sortOrderToken = (colIndex, dir) => {
  const prefix = 0 === colIndex ? 'i' : 'v'

  return `${prefix}${1 === dir ? 'asc' : 'desc'}`
}

// Everything the researcher sees once the numbers are crunched: the strip that picks the measure
// the items are read in, then a chart over them, then the items as a table. What only this half needs is derived here, so a question with nothing
// to draw never pays for it.
const BlockBody = ({ state, ...props }) => {
  const {
    id,
    qType,
    questionKey,
    link,
    visualPosition,
    format,
    answer,
    batches,
    itemsPerSet,
    maxDiffType,
    reportPercentDecimals,
    globalChartMode,
    globalZoomLevel,
    colorTheme,
    chartLegendMode,
    confidenceLevel,
    confidenceLevels,
    components,
    features,
    specialExport,
    isReportXpert,
    handlers = {}
  } = props

  const {
    viewMode,
    chartMode,
    zoomLevel,
    sortOrder,
    modelChartMode,
    onChartReady,
    onChartModeChange,
    onViewModeChange,
    onZoomChange,
    onSortChange
  } = state

  const question = { ...props, type: qType, chartMode, viewMode, zoomLevel, sortOrder }

  const percentDecimals = resolvePercentDecimals(format, reportPercentDecimals)

  const tableProps = calcTableProps({
    answer,
    batches,
    viewMode,
    format,
    percentDecimals,
    sortOrder,
    onSort: (colIndex, dir) => onSortChange(sortOrderToken(colIndex, dir))
  })

  // The download service is handed the whole payload; the block only runs the download.
  const onExportClick = useCallback(
    ({ event, fileType, exportType, exportMethod, exportMethodText } = {}, callback) => {
      const download = calcDownloadParams({
        fileType,
        exportType,
        exportMethod,
        exportMethodText,
        itemsPerSet,
        link,
        questionKey,
        visualPosition
      })

      handlers.onExport?.(
        download.fileType,
        download.exportType,
        download.exportOptions,
        callback,
        Boolean(event?.shiftKey)
      )
    },
    [handlers, itemsPerSet, link, questionKey, visualPosition]
  )

  return (
    <>
      <ItemsControls {...calcControlsProps({ ...question, handlers })} />

      {isComponentOn(components, 'controls') && (
        <Controls
          link={link}
          format={format}
          answer={answer || {}}
          viewMode={viewMode}
          features={features}
          itemsPerSet={itemsPerSet}
          maxDiffType={maxDiffType}
          specialExport={specialExport}
          isReportXpert={isReportXpert}
          onViewModeChange={onViewModeChange}
          onExport={onExportClick}
        />
      )}
      {isComponentOn(components, 'chart') && !isNoneChartMode(modelChartMode, globalChartMode) && (
        <ChartPlaceholder isLoading={'loading' == answer}>
          <Chart
            questionId={id}
            questionKey={questionKey}
            qType={qType}
            batches={batches}
            answer={answer}
            sortOrder={sortOrder}
            viewMode={viewMode}
            itemsPerSet={itemsPerSet}
            chartMode={chartMode}
            modelChartMode={modelChartMode}
            globalChartMode={globalChartMode}
            zoomLevel={zoomLevel}
            globalZoomLevel={globalZoomLevel}
            colorTheme={colorTheme}
            chartLegendMode={chartLegendMode}
            confidenceLevel={confidenceLevel}
            confidenceLevels={confidenceLevels}
            features={features}
            onChartModeChange={onChartModeChange}
            onZoomChange={(key, level) => onZoomChange(level)}
            onChartReady={onChartReady}
          />
        </ChartPlaceholder>
      )}
      {isComponentOn(components, 'table') && <Table id={id} {...tableProps} />}
    </>
  )
}

BlockBody.displayName = 'MaxDiffBlockBody'

// The max-diff block. The strip that picks the measure the items are read in, a chart over the
// items, then the items as a table. A max-diff is never word-cloud-eligible, as on master.
//
// block.coffee owns the model, the mount and the report-param write. Everything the researcher sees
// and moves is here, and the block answers the exporters through the api it hands up.
const Block = props => {
  const {
    qType,
    questionKey,
    schemaKey,
    format,
    modelChartMode,
    answer,
    batches,
    itemsPerSet: declaredItemsPerSet,
    maxDiffType: declaredMaxDiffType,
    params,
    paramsRevision,
    calcBadData,
    onBadData,
    onRunComplete,
    onReady,
    onStateChange
  } = props

  // What the question declares about its own shape: the mode decides which surfaces are offered at
  // all, and the set size names the first measure. Neither is guaranteed to arrive whole.
  const maxDiffType = parseInt(declaredMaxDiffType || '0', 10)
  const itemsPerSet = declaredItemsPerSet || DEFAULT_ITEMS_PER_SET

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useMaxDiffState({
    params,
    paramsRevision,
    questionKey,
    type: qType,
    format,
    modelChartMode,
    answer,
    batches,
    itemsPerSet,
    onReady,
    onStateChange
  })

  // the question's own rule, applied to the payload on screen -- read here rather than taken as
  // a flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  return (
    <BlockWrapper {...props}>
      <DataRun
        answer={answer}
        badData={badData}
        type={qType}
        jobType="maxdiff"
        questionKey={questionKey}
        schemaKey={schemaKey}
        icon={RUN_ICON}
        note={RUN_NOTE}
        colorTheme={getColorTheme()}
        calcBadData={calcBadData}
        onBadData={onBadData}
        onComplete={onRunComplete}
      />
      {/* while the numbers have not been crunched there is nothing else to show */}
      {badData ? null : <BlockBody {...props} itemsPerSet={itemsPerSet} maxDiffType={maxDiffType} state={state} />}
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  id: PropTypes.string,
  qType: PropTypes.string,
  questionKey: PropTypes.string,
  schemaKey: PropTypes.string,
  variant: PropTypes.string,
  questionType: PropTypes.string,
  link: PropTypes.string,
  visualPosition: PropTypes.number,
  format: PropTypes.object,
  // the chart mode the question declares in its data
  modelChartMode: PropTypes.string,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  batches: PropTypes.array,
  itemsPerSet: PropTypes.number,
  maxDiffType: PropTypes.number,
  reportPercentDecimals: PropTypes.number,
  globalChartMode: PropTypes.string,
  globalZoomLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  components: PropTypes.object,
  features: PropTypes.object,
  specialExport: PropTypes.object,
  isReportXpert: PropTypes.bool,
  paramsRevision: PropTypes.number,
  params: PropTypes.object,
  handlers: PropTypes.object,
  calcBadData: PropTypes.func,
  onBadData: PropTypes.func,
  onRunComplete: PropTypes.func,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func
}

// the body is handed the block's own props, plus the state the block owns
BlockBody.propTypes = { ...Block.propTypes, state: PropTypes.object }

export default Block
